import {
  Flex,
  Checkbox,
  Menu,
  MenuButton,
  IconButton,
  MenuList,
  MenuItem,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { TierPrice, InquiryWithContext } from '@texas/api/endpoints/inquiryApi';
import { Icons } from '@texas/components/shared/Icons';
import { useTranslation } from 'react-i18next';
import { AcceptedInquiryPriceMeta } from '../AcceptedInquiryPriceMeta';
import { AutoUpdateInquiryPrice } from '../AutoUpdateInquiryPrice';
import { priceWithFees } from '../inquiryPriceHelpers';
import { isTierLocked } from '../useInquiryTierPriceSelection';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { InquiryPriceRoundButton } from '../InquiryPriceRoundButton';

interface TierCellProps {
  tp: TierPrice;
  inq: InquiryWithContext;
  currencyCode: string;
  showWithFees: boolean;
  comparePreviousPrice: boolean;
  isSelected: boolean;
  priceRound: number;
  onToggleCell: (tpId: number) => void;
  onCreateVersion: (tpId: number) => void;
  onAccept: (tpId: number) => void;
  onReject: (tpId: number) => void;
  onReview: (tpId: number) => void;
  onOpenHistory: (tpId: number, currencyCode: string) => void;
}

export function TierCell({
  tp,
  inq,
  currencyCode,
  showWithFees,
  comparePreviousPrice,
  isSelected,
  priceRound,
  onToggleCell,
  onCreateVersion,
  onAccept,
  onReject,
  onReview,
  onOpenHistory,
}: TierCellProps) {
  const { t } = useTranslation();
  const isTerminal = isTierLocked(tp);

  return (
    <Flex alignItems="start" gap={1} w="full" minW={0}>
      <Flex
        direction="column"
        flex={1}
        minW={0}
        whiteSpace="normal"
        overflowWrap="anywhere"
      >
        <Flex align="center" gap={1} minW={0}>
          <Tooltip label={t('inquiry.lockedReady')} isDisabled={!isTerminal}>
            <AutoUpdateInquiryPrice
              key={tp.id}
              tierId={tp.id}
              inquiryId={inq.id}
              currentPrice={tp.price ?? null}
              currencyCode={currencyCode}
              isDisabled={isTerminal}
            />
          </Tooltip>
          <InquiryPriceRoundButton
            round={priceRound}
            onOpenHistory={() => onOpenHistory(tp.id, currencyCode)}
          />
        </Flex>
        {showWithFees && <Fees inq={inq} tp={tp} currencyCode={currencyCode} />}
        {comparePreviousPrice && (
          <PreviousPriceComparison
            currentPrice={tp.price}
            previousPrice={tp.basedOnPrice}
            currencyCode={currencyCode}
          />
        )}

        <AcceptedInquiryPriceMeta
          acceptedDate={tp.acceptedDate}
          acceptedByUserName={tp.acceptedByUserName}
          rejectedAt={tp.rejectedAt}
        />
      </Flex>
      <Checkbox
        isChecked={isSelected}
        ml="auto"
        mt="1.5"
        onChange={() => onToggleCell(tp.id)}
        size="sm"
        flexShrink={0}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      />

      <Menu isLazy={true}>
        <MenuButton
          as={IconButton}
          aria-label={t('general.actions')}
          icon={<Icons.DotsHorizontal boxSize={3} />}
          size="xs"
          variant="ghost"
          flexShrink={0}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
        <MenuList>
          {tp.price != null &&
            (tp.acceptedDate == null || tp.rejectedAt != null) && (
              <MenuItem onClick={() => onAccept(tp.id)}>
                {t('inquiry.acceptPrice')}
              </MenuItem>
            )}
          {tp.rejectedAt == null && (
            <MenuItem onClick={() => onReject(tp.id)}>
              {t('inquiry.rejectPrice')}
            </MenuItem>
          )}
          {isTerminal && (
            <MenuItem onClick={() => onReview(tp.id)}>
              {t('inquiry.setUnderReview')}
            </MenuItem>
          )}
          {isTerminal && (
            <MenuItem onClick={() => onCreateVersion(tp.id)}>
              {t('inquiry.newPriceRound')}
            </MenuItem>
          )}
          <MenuItem onClick={() => onOpenHistory(tp.id, currencyCode)}>
            {t('inquiry.openHistory')}
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
}

function PreviousPriceComparison({
  currentPrice,
  previousPrice,
  currencyCode,
}: {
  currentPrice: number | null;
  previousPrice: number | null;
  currencyCode: string;
}) {
  const { t } = useTranslation();
  if (currentPrice == null || previousPrice == null) return null;

  const isBetter = currentPrice < previousPrice;
  const isSame = currentPrice === previousPrice;
  const Icon = isSame
    ? Icons.ArrowRight
    : isBetter
    ? Icons.ChevronDown
    : Icons.ChevronUp;
  const color = isSame ? 'gray.500' : isBetter ? 'green.400' : 'red.400';

  return (
    <Flex align="center" gap={1} mt={0.5} color={color}>
      <Icon boxSize={3} />
      <Text fontSize="2xs" whiteSpace="normal" overflowWrap="anywhere">
        {t('general.previousPrice')}: {previousPrice} {currencyCode}
      </Text>
    </Flex>
  );
}

function Fees({
  inq,
  tp,
  currencyCode,
}: {
  inq: InquiryWithContext;
  tp: TierPrice;
  currencyCode: string;
}) {
  const {
    handlingFeesData: [handlingFees],
    freightCostsData: [freightCosts],
  } = useMetadata();

  if (tp.price == null) return null;

  const hFee = handlingFees.find((f) => f.id === inq.handlingFeeId)?.value ?? 0;
  const fFee = freightCosts.find((f) => f.id === inq.freightCostId)?.value ?? 0;

  return (
    <Text fontSize="xs" mt={1} whiteSpace="normal" overflowWrap="anywhere">
      {priceWithFees(tp.price, hFee, fFee).toFixed(2)} {currencyCode}
    </Text>
  );
}
